export type CatalogMediaDelivery = {
  contract: "POS_CATALOG_MEDIA_DELIVERY_V1";
  media_asset_id: string;
  content_revision: string;
  mime_type: string | null;
  byte_size: number | null;
  width_px: number | null;
  height_px: number | null;
  signed_url: string;
  expires_in_seconds: number;
};

export type CatalogMediaReference = {
  media_asset_id: string;
  content_revision: string;
};

export type CatalogMediaBatchDelivery = {
  contract: "POS_CATALOG_MEDIA_DELIVERY_BATCH_V1";
  requested_count: number;
  delivered_count: number;
  unavailable_count: number;
  items: CatalogMediaDelivery[];
};

type GatewayEnvelope<T> = {
  ok: boolean;
  data?: T;
  error?: string;
};

export type CatalogMediaBatchResolveResult = {
  ok: boolean;
  delivery: CatalogMediaBatchDelivery | null;
  error: string | null;
};

export type CatalogMediaDownloadResult = {
  ok: boolean;
  delivery: CatalogMediaDelivery | null;
  blob: Blob | null;
  error: string | null;
};

type ClientOptions = {
  baseUrl: string;
  timeoutMs?: number;
};

function mediaKey(mediaAssetId: string, contentRevision: string) {
  return `${mediaAssetId}:${contentRevision.toLowerCase()}`;
}

export class PosCatalogMediaGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 20_000;
  }

  async resolveBatch(input: {
    deviceSecret: string;
    sessionToken: string;
    media: CatalogMediaReference[];
  }): Promise<CatalogMediaBatchResolveResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/catalog-media/batch`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          device_secret: input.deviceSecret,
          session_token: input.sessionToken,
          media: input.media,
        }),
        signal: controller.signal,
      });
      const envelope = (await response.json()) as GatewayEnvelope<CatalogMediaBatchDelivery>;
      if (!response.ok || !envelope.ok || !envelope.data) {
        return {
          ok: false,
          delivery: null,
          error: envelope.error ?? `HTTP_${response.status}`,
        };
      }

      const delivery = envelope.data;
      if (delivery.contract !== "POS_CATALOG_MEDIA_DELIVERY_BATCH_V1" || !Array.isArray(delivery.items)) {
        return {
          ok: false,
          delivery: null,
          error: "CATALOG_MEDIA_BATCH_DELIVERY_INVALID",
        };
      }

      const requested = new Set(
        input.media.map((media) => mediaKey(media.media_asset_id, media.content_revision)),
      );
      for (const item of delivery.items) {
        if (
          item.contract !== "POS_CATALOG_MEDIA_DELIVERY_V1" ||
          !requested.has(mediaKey(item.media_asset_id, item.content_revision)) ||
          !item.signed_url
        ) {
          return {
            ok: false,
            delivery: null,
            error: "CATALOG_MEDIA_BATCH_DELIVERY_REFERENCE_MISMATCH",
          };
        }
      }

      return { ok: true, delivery, error: null };
    } catch (error) {
      return {
        ok: false,
        delivery: null,
        error: error instanceof DOMException && error.name === "AbortError"
          ? "CATALOG_MEDIA_BATCH_RESOLVE_TIMEOUT"
          : "CATALOG_MEDIA_BATCH_RESOLVE_FAILED",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async downloadDelivery(delivery: CatalogMediaDelivery): Promise<CatalogMediaDownloadResult> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const binaryResponse = await fetch(delivery.signed_url, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      if (!binaryResponse.ok) {
        return {
          ok: false,
          delivery,
          blob: null,
          error: `CATALOG_MEDIA_BINARY_HTTP_${binaryResponse.status}`,
        };
      }

      const blob = await binaryResponse.blob();
      return { ok: true, delivery, blob, error: null };
    } catch (error) {
      return {
        ok: false,
        delivery,
        blob: null,
        error: error instanceof DOMException && error.name === "AbortError"
          ? "CATALOG_MEDIA_DOWNLOAD_TIMEOUT"
          : "CATALOG_MEDIA_DOWNLOAD_FAILED",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async download(input: {
    deviceSecret: string;
    sessionToken: string;
    mediaAssetId: string;
    contentRevision: string;
  }): Promise<CatalogMediaDownloadResult> {
    const resolved = await this.resolveBatch({
      deviceSecret: input.deviceSecret,
      sessionToken: input.sessionToken,
      media: [{
        media_asset_id: input.mediaAssetId,
        content_revision: input.contentRevision,
      }],
    });
    const delivery = resolved.delivery?.items[0] ?? null;
    if (!resolved.ok || !delivery) {
      return {
        ok: false,
        delivery: null,
        blob: null,
        error: resolved.error ?? "CATALOG_MEDIA_NOT_AVAILABLE",
      };
    }
    return this.downloadDelivery(delivery);
  }
}
